import React, { useState, useEffect } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  useToast,
  Heading,
  useColorModeValue,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Spinner,
  Alert,
  AlertIcon,
  Radio,
  RadioGroup,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Textarea,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Progress,
  SimpleGrid,
} from '@chakra-ui/react'
import axios from 'axios'
import { hasPermission, type Role } from '../config/permissions'

interface AISettings {
  confidenceThreshold: number
  maxSearchResults: number
  autoImprovement: boolean
  embeddingProvider?: 'xenova' | 'huggingface'
  huggingfaceApiKey?: string | null
  embeddingUsageCount?: number
  embeddingUsageResetDate?: string
  llmProvider?: 'gemini' | 'openai' | 'claude'
  llmModel?: string
  llmApiKey?: string | null
  llmTemperature?: number
  systemVersion?: string
  tokenUsage?: {
    today: {
      totalTokens: number
      inputTokens: number
      outputTokens: number
      queriesCount: number
    }
    monthly: {
      totalTokens: number
      inputTokens: number
      outputTokens: number
      queriesCount: number
    }
    dailyLimit: {
      requests: number
      tokens: number
    }
    // 하위 호환성
    totalTokens: number
    inputTokens: number
    outputTokens: number
    queriesCount: number
  }
  updatedAt?: string
}

const SettingsPage: React.FC = () => {
  const toast = useToast()
  const cardBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.600')

  // 권한 확인
  const userRole = (localStorage.getItem('userRole') || 'admin') as Role
  const canConfigureAI = hasPermission(userRole, 'SETTINGS_AI_CONFIG')
  const canConfigureEmbedding = hasPermission(userRole, 'SETTINGS_EMBEDDING_CONFIG')
  const canEditSystemConfig = hasPermission(userRole, 'SETTINGS_EDIT') // 시스템 설정 편집 (admin만)

  const [settings, setSettings] = useState<AISettings>({
    confidenceThreshold: 0.7,
    maxSearchResults: 5,
    autoImprovement: true,
    embeddingProvider: 'xenova',
    huggingfaceApiKey: null,
    embeddingUsageCount: 0,
    llmProvider: 'gemini',
    llmModel: 'gemini-2.5-flash',
    llmApiKey: null,
    llmTemperature: 0.7,
    systemVersion: '1.3.1',
  })
  const [versionInfo, setVersionInfo] = useState<any>({
    systemVersion: '1.3.1',
    langGraphVersion: '0.0.26',
    langChainVersion: '0.3.35',
    geminiVersion: '2.0 Flash',
  })
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [showApiKeyInput, setShowApiKeyInput] = useState(false)

  // 시스템 설정 (동의어, 패턴, 시설파일패턴, 부대시설, 쿼리필터, 시스템메시지) - admin만
  // 🆕 v9.3: common_facility_keywords 제거 → facility_file_patterns로 통합 (2025-12-05)
  // 🆕 v9.9: query_filter_patterns, system_messages, query_classifier_config 추가 (2025-12-08)
  // 🆕 v10.21: vr_spots, layout_links 제거 → 사용하지 않는 VR 기능 삭제 (2025-12-19)
  const [systemConfigs, setSystemConfigs] = useState<{[key: string]: string}>({
    synonyms: '',
    attribute_patterns: '',
    facility_file_patterns: '',
    utilities: '',
    query_filter_patterns: '',
    system_messages: '',
    query_classifier_config: ''
  })
  const [systemConfigLoading, setSystemConfigLoading] = useState<{[key: string]: boolean}>({})
  const [systemConfigSaving, setSystemConfigSaving] = useState<{[key: string]: boolean}>({})
  const [systemConfigMeta, setSystemConfigMeta] = useState<{[key: string]: any}>({})
  const [systemConfigTab, setSystemConfigTab] = useState(0)

  // 🆕 v9.3: common_facility_keywords 제거됨 - facility_file_patterns와 통합 (2025-12-05)
  // 쿼리 필터는 DB 저장 후 캐시 리로드 → 재시작 없이 적용
  const CONFIG_KEYS = {
    synonyms: { title: '🔤 동의어 매핑', description: '키워드 확장용 동의어 사전 (그룹 기반)' },
    facility_file_patterns: { 
      title: '🗂️ 시설별 파일 패턴', 
      description: '시설 키워드 → 파일 패턴 매핑 (그룹 기반) - 공통 시설 키워드 포함' 
    },
    utilities: { 
      title: '💰 부대시설 요금', 
      description: '전기료, 주차료, 냉난방료 등 부대시설 요금 정보' 
    },
    query_filter_patterns: {
      title: '🛡️ 쿼리 필터',
      description: '보안/무의미/스팸 정규식. 저장하면 서버 재시작 없이 즉시 적용됩니다.'
    }
  }

  // AI 설정 및 버전 정보 로드
  useEffect(() => {
    loadSettings()
    loadVersionInfo()
  }, [])

  // 시스템 설정 초기 로드
  useEffect(() => {
    if (canEditSystemConfig) {
      Object.keys(CONFIG_KEYS).forEach(key => fetchSystemConfig(key))
    }
  }, [canEditSystemConfig])

  const loadSettings = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/settings')
      if (response.data.success) {
        setSettings(response.data.data)
      }
    } catch (error) {
      console.error('설정 로드 오류:', error)
      toast({
        title: '설정 로드 실패',
        description: '기본 설정값을 사용합니다.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const loadVersionInfo = async () => {
    try {
      const response = await axios.get('/api/version')
      if (response.data.success) {
        setVersionInfo(response.data.data)
      }
    } catch (error) {
      console.error('버전 정보 로드 오류:', error)
      // 실패해도 기본값 유지
    }
  }

  const handleSaveSettings = async () => {
    try {
      setIsSaving(true)
      const response = await axios.post('/settings', settings)
      
      if (response.data.success) {
        toast({
          title: 'AI 설정 저장 완료',
          description: '설정이 성공적으로 저장되었습니다.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        // 최신 설정 다시 로드
        await loadSettings()
      }
    } catch (error: any) {
      console.error('설정 저장 오류:', error)
      toast({
        title: 'AI 설정 저장 실패',
        description: error.response?.data?.message || '설정 저장 중 오류가 발생했습니다.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsSaving(false)
    }
  }

  // 시스템 설정 가져오기
  const fetchSystemConfig = async (key: string) => {
    try {
      setSystemConfigLoading(prev => ({ ...prev, [key]: true }))
      const response = await axios.get(`/graph/system-config/${key}`, { withCredentials: true })
      
      if (response.data.success) {
        setSystemConfigs(prev => ({
          ...prev,
          [key]: JSON.stringify(response.data.config, null, 2)
        }))
        setSystemConfigMeta(prev => ({
          ...prev,
          [key]: {
            updated_at: response.data.updated_at,
            updated_by: response.data.updated_by
          }
        }))
      }
    } catch (error: any) {
      toast({
        title: '시스템 설정 조회 실패',
        description: error.response?.data?.message || '오류 발생',
        status: 'error',
        duration: 3000
      })
    } finally {
      setSystemConfigLoading(prev => ({ ...prev, [key]: false }))
    }
  }

  // 시스템 설정 저장
  const saveSystemConfig = async (key: string) => {
    try {
      const config = JSON.parse(systemConfigs[key])
      setSystemConfigSaving(prev => ({ ...prev, [key]: true }))
      
      const response = await axios.put(
        `/graph/system-config/${key}`,
        { config },
        { withCredentials: true }
      )
      
      if (response.data.success) {
        toast({
          title: '시스템 설정 저장 완료',
          description: '설정이 즉시 적용되었습니다.',
          status: 'success',
          duration: 3000
        })
        await fetchSystemConfig(key)
      }
    } catch (error: any) {
      if (error instanceof SyntaxError) {
        toast({
          title: 'JSON 형식 오류',
          description: 'JSON 형식이 올바르지 않습니다.',
          status: 'error',
          duration: 5000
        })
      } else {
        toast({
          title: '시스템 설정 저장 실패',
          description: error.response?.data?.message || '오류 발생',
          status: 'error',
          duration: 5000
        })
      }
    } finally {
      setSystemConfigSaving(prev => ({ ...prev, [key]: false }))
    }
  }

  // 시스템 설정 초기 로드
  useEffect(() => {
    if (canEditSystemConfig) {
      Object.keys(CONFIG_KEYS).forEach(key => fetchSystemConfig(key))
    }
  }, [canEditSystemConfig])

  // JSON 유효성 검증
  const isSystemConfigJsonValid = (key: string) => {
    try {
      JSON.parse(systemConfigs[key])
      return true
    } catch {
      return false
    }
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* 페이지 헤더 */}
      <Box>
        <Heading size="lg" color="bexco.700" mb={2}>
          시스템 설정
        </Heading>
        <Text color="gray.600">
          BEXCO LangGraph RAG 시스템의 설정을 관리하세요.
        </Text>
      </Box>

      {/* Accordion으로 카드들 감싸기 */}
      <Accordion allowMultiple defaultIndex={[0, 3]}>
        
        {/* 시스템 정보 */}
        <AccordionItem border="1px" borderColor={borderColor} borderRadius="md" mb={4} bg={cardBg}>
          <h2>
            <AccordionButton _expanded={{ bg: useColorModeValue('gray.50', 'gray.700') }}>
              <Box flex="1" textAlign="left">
                <Heading size="md">📊 시스템 정보</Heading>
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4}>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <Text>시스템 버전</Text>
                <Text fontWeight="medium">v{versionInfo.systemVersion}</Text>
              </HStack>
              <HStack justify="space-between">
                <Text>LangGraph 버전</Text>
                <Text fontWeight="medium">{versionInfo.langGraphVersion}</Text>
              </HStack>
              <HStack justify="space-between">
                <Text>LangChain 버전</Text>
                <Text fontWeight="medium">{versionInfo.langChainVersion}</Text>
              </HStack>
              <HStack justify="space-between">
                <Text>LLM 제공자</Text>
                <Text fontWeight="medium" color="purple.500" textTransform="capitalize">
                  {settings.llmProvider || 'Gemini'} ({settings.llmModel || 'gemini-2.5-flash'})
                </Text>
              </HStack>
              <HStack justify="space-between">
                <Text>ChromaDB</Text>
                <Text fontWeight="medium" color="green.500">정상</Text>
              </HStack>
            </VStack>
          </AccordionPanel>
        </AccordionItem>

        {/* LLM 제공자 설정 - admin만 가능 */}
        {canConfigureAI && (
          <AccordionItem border="1px" borderColor={borderColor} borderRadius="md" mb={4} bg={cardBg}>
            <h2>
              <AccordionButton _expanded={{ bg: useColorModeValue('gray.50', 'gray.700') }}>
                <Box flex="1" textAlign="left">
                  <Heading size="md">🤖 LLM 제공자 설정</Heading>
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
            {isLoading ? (
              <Box textAlign="center" py={8}>
                <Spinner size="lg" color="bexco.500" />
                <Text mt={4} color="gray.600">설정을 불러오는 중...</Text>
              </Box>
            ) : (
              <VStack spacing={6} align="stretch">
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">
                    답변 생성에 사용할 대규모 언어 모델을 선택하세요
                  </Text>
                </Alert>
              
              <FormControl>
                <FormLabel fontWeight="bold">LLM 제공자</FormLabel>
                <RadioGroup 
                  value={settings.llmProvider || 'gemini'}
                  onChange={(value) => {
                    const newProvider = value as 'gemini' | 'openai' | 'claude';
                    let defaultModel = 'gemini-2.5-flash';
                    if (newProvider === 'openai') defaultModel = 'gpt-4-turbo';
                    else if (newProvider === 'claude') defaultModel = 'claude-3-sonnet-20240229';
                    
                    setSettings({
                      ...settings,
                      llmProvider: newProvider,
                      llmModel: defaultModel
                    });
                  }}
                >
                  <Stack direction="column" spacing={3}>
                    <Box 
                      p={4} 
                      border="1px" 
                      borderColor={settings.llmProvider === 'gemini' ? 'blue.500' : borderColor}
                      borderRadius="md"
                      bg={settings.llmProvider === 'gemini' ? 'blue.50' : 'transparent'}
                    >
                      <Radio value="gemini" size="lg">
                        <VStack align="start" spacing={1} ml={2}>
                          <HStack>
                            <Text fontWeight="medium">Google Gemini</Text>
                            <Badge colorScheme="blue">사용 중</Badge>
                          </HStack>
                          <Text fontSize="sm" color="gray.600">
                            • 빠르고 정확한 답변 생성
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            • 무료 할당량: 1,500 req/day
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            • 모델: {settings.llmProvider === 'gemini' ? settings.llmModel : 'gemini-2.5-flash'}
                          </Text>
                        </VStack>
                      </Radio>
                    </Box>
                    
                    <Box 
                      p={4} 
                      border="1px" 
                      borderColor={settings.llmProvider === 'openai' ? 'green.500' : borderColor}
                      borderRadius="md"
                      bg={settings.llmProvider === 'openai' ? 'green.50' : 'transparent'}
                      opacity={0.6}
                    >
                      <Radio value="openai" size="lg" isDisabled>
                        <VStack align="start" spacing={1} ml={2}>
                          <HStack>
                            <Text fontWeight="medium">OpenAI GPT</Text>
                            <Badge colorScheme="orange">준비 중</Badge>
                          </HStack>
                          <Text fontSize="sm" color="gray.600">
                            • GPT-4, GPT-3.5 Turbo
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            • 향후 지원 예정
                          </Text>
                        </VStack>
                      </Radio>
                    </Box>
                    
                    <Box 
                      p={4} 
                      border="1px" 
                      borderColor={settings.llmProvider === 'claude' ? 'purple.500' : borderColor}
                      borderRadius="md"
                      bg={settings.llmProvider === 'claude' ? 'purple.50' : 'transparent'}
                      opacity={0.6}
                    >
                      <Radio value="claude" size="lg" isDisabled>
                        <VStack align="start" spacing={1} ml={2}>
                          <HStack>
                            <Text fontWeight="medium">Anthropic Claude</Text>
                            <Badge colorScheme="orange">준비 중</Badge>
                          </HStack>
                          <Text fontSize="sm" color="gray.600">
                            • Claude 3 Opus, Sonnet
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            • 향후 지원 예정
                          </Text>
                        </VStack>
                      </Radio>
                    </Box>
                  </Stack>
                </RadioGroup>
              </FormControl>

              {settings.llmProvider === 'gemini' && (
                <>
                  {/* 🆕 v10.9: 개선된 토큰 사용량 UI */}
                  {settings.tokenUsage && (
                    <Box p={4} border="1px" borderColor={borderColor} borderRadius="md" bg={useColorModeValue('gray.50', 'gray.700')}>
                      <Heading size="sm" mb={4}>📊 Gemini API 사용량</Heading>
                      
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        {/* 오늘 사용량 */}
                        <Box p={4} bg={useColorModeValue('blue.50', 'blue.900')} borderRadius="md" border="2px" borderColor="blue.300">
                          <Text fontWeight="bold" color="blue.600" mb={2}>📅 오늘 (KST 기준)</Text>
                          <VStack align="start" spacing={2}>
                            <HStack justify="space-between" w="100%">
                              <Text fontSize="sm">요청 수</Text>
                              <Text fontWeight="bold">
                                {(settings.tokenUsage.today?.queriesCount || 0).toLocaleString()} / {(settings.tokenUsage.dailyLimit?.requests || 1500).toLocaleString()}
                              </Text>
                            </HStack>
                            <Progress 
                              value={((settings.tokenUsage.today?.queriesCount || 0) / (settings.tokenUsage.dailyLimit?.requests || 1500)) * 100} 
                              colorScheme={((settings.tokenUsage.today?.queriesCount || 0) / (settings.tokenUsage.dailyLimit?.requests || 1500)) > 0.8 ? 'red' : 'blue'}
                              size="sm" 
                              borderRadius="full"
                              w="100%"
                            />
                            <HStack justify="space-between" w="100%">
                              <Text fontSize="sm">토큰 수</Text>
                              <Text fontWeight="bold">
                                {(settings.tokenUsage.today?.totalTokens || 0).toLocaleString()} / {(settings.tokenUsage.dailyLimit?.tokens || 1500000).toLocaleString()}
                              </Text>
                            </HStack>
                            <Progress 
                              value={((settings.tokenUsage.today?.totalTokens || 0) / (settings.tokenUsage.dailyLimit?.tokens || 1500000)) * 100} 
                              colorScheme={((settings.tokenUsage.today?.totalTokens || 0) / (settings.tokenUsage.dailyLimit?.tokens || 1500000)) > 0.8 ? 'red' : 'green'}
                              size="sm" 
                              borderRadius="full"
                              w="100%"
                            />
                            <Text fontSize="xs" color="gray.500">
                              남은 요청: {((settings.tokenUsage.dailyLimit?.requests || 1500) - (settings.tokenUsage.today?.queriesCount || 0)).toLocaleString()}회
                            </Text>
                          </VStack>
                        </Box>
                        
                        {/* 월간 사용량 (30일) */}
                        <Box p={4} bg={useColorModeValue('purple.50', 'purple.900')} borderRadius="md">
                          <Text fontWeight="bold" color="purple.600" mb={2}>📆 최근 30일</Text>
                          <VStack align="start" spacing={2}>
                            <HStack justify="space-between" w="100%">
                              <Text fontSize="sm">총 쿼리</Text>
                              <Text fontWeight="bold">{(settings.tokenUsage.monthly?.queriesCount || settings.tokenUsage.queriesCount || 0).toLocaleString()}회</Text>
                            </HStack>
                            <HStack justify="space-between" w="100%">
                              <Text fontSize="sm">총 토큰</Text>
                              <Text fontWeight="bold">{(settings.tokenUsage.monthly?.totalTokens || settings.tokenUsage.totalTokens || 0).toLocaleString()}</Text>
                            </HStack>
                            <HStack justify="space-between" w="100%">
                              <Text fontSize="sm">일평균 쿼리</Text>
                              <Text fontWeight="bold">{Math.round((settings.tokenUsage.monthly?.queriesCount || settings.tokenUsage.queriesCount || 0) / 30).toLocaleString()}회</Text>
                            </HStack>
                            <HStack justify="space-between" w="100%">
                              <Text fontSize="sm">일평균 토큰</Text>
                              <Text fontWeight="bold">{Math.round((settings.tokenUsage.monthly?.totalTokens || settings.tokenUsage.totalTokens || 0) / 30).toLocaleString()}</Text>
                            </HStack>
                          </VStack>
                        </Box>
                      </SimpleGrid>
                      
                      <Alert status="info" mt={4} borderRadius="md" size="sm">
                        <AlertIcon />
                        <Text fontSize="xs">
                          무료 한도는 매일 KST 00:00 (자정)에 초기화됩니다.
                        </Text>
                      </Alert>
                    </Box>
                  )}

                  <FormControl>
                    <FormLabel>
                      Gemini 모델 버전
                      <Text fontSize="sm" color="gray.500" fontWeight="normal">
                        사용할 Gemini 모델을 선택하세요
                      </Text>
                    </FormLabel>
                    <RadioGroup 
                      value={settings.llmModel}
                      onChange={(value) => setSettings({ ...settings, llmModel: value })}
                    >
                      <Stack spacing={2}>
                        <Radio value="gemini-2.5-flash">
                          Gemini 2.5 Flash - 빠른 응답 (권장)
                        </Radio>
                        <Radio value="gemini-2.5-pro">
                          Gemini 2.5 Pro - 고품질 답변
                        </Radio>
                        <Radio value="gemini-2.5-flash-lite">
                          Gemini 2.5 Flash Lite - 경량/저비용
                        </Radio>
                      </Stack>
                    </RadioGroup>
                  </FormControl>

                  <FormControl>
                    <FormLabel>
                      LLM 온도 (Temperature)
                      <Text fontSize="sm" color="gray.500" fontWeight="normal">
                        답변의 창의성 조절 (0: 정확, 1: 창의적)
                      </Text>
                    </FormLabel>
                    <HStack spacing={4}>
                      <Input 
                        type="number" 
                        value={settings.llmTemperature}
                        onChange={(e) => setSettings({
                          ...settings,
                          llmTemperature: parseFloat(e.target.value)
                        })}
                        min="0" 
                        max="2" 
                        step="0.1" 
                        width="100px"
                      />
                      <Text fontSize="sm" color="gray.600">
                        현재: {settings.llmTemperature} (권장: 0.7)
                      </Text>
                    </HStack>
                  </FormControl>
                </>
              )}
              
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  LLM 제공자를 변경하면 답변 품질과 속도가 달라질 수 있습니다. Gemini는 현재 안정적으로 사용 가능합니다.
                </Text>
              </Alert>
            </VStack>
          )}
            </AccordionPanel>
          </AccordionItem>
        )}

        {/* 임베딩 제공자 설정 - admin만 가능 */}
        {canConfigureEmbedding && (
          <AccordionItem border="1px" borderColor={borderColor} borderRadius="md" mb={4} bg={cardBg}>
            <h2>
              <AccordionButton _expanded={{ bg: useColorModeValue('gray.50', 'gray.700') }}>
                <Box flex="1" textAlign="left">
                  <Heading size="md">🔍 임베딩 제공자 설정</Heading>
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
            <VStack spacing={6} align="stretch">
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  벡터 검색을 위한 임베딩 생성 방식을 선택하세요
                </Text>
              </Alert>
              
              <FormControl>
                <FormLabel fontWeight="bold">임베딩 제공자</FormLabel>
                <RadioGroup 
                  value={settings.embeddingProvider || 'xenova'}
                  onChange={(value) => {
                    setSettings({
                      ...settings,
                      embeddingProvider: value as 'xenova' | 'huggingface'
                    })
                    setShowApiKeyInput(value === 'huggingface')
                  }}
                >
                  <Stack direction="column" spacing={3}>
                    <Box 
                      p={4} 
                      border="1px" 
                      borderColor={settings.embeddingProvider === 'xenova' ? 'bexco.500' : borderColor}
                      borderRadius="md"
                      bg={settings.embeddingProvider === 'xenova' ? 'bexco.50' : 'transparent'}
                    >
                      <Radio value="xenova" size="lg">
                        <VStack align="start" spacing={1} ml={2}>
                          <HStack>
                            <Text fontWeight="medium">Xenova (로컬)</Text>
                            <Badge colorScheme="green">무료</Badge>
                            <Badge colorScheme="blue">추천</Badge>
                          </HStack>
                          <Text fontSize="sm" color="gray.600">
                            • 완전 무료, 무제한 사용
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            • 로컬 처리로 프라이버시 보호
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            • 384차원 임베딩
                          </Text>
                        </VStack>
                      </Radio>
                    </Box>
                    
                    <Box 
                      p={4} 
                      border="1px" 
                      borderColor={settings.embeddingProvider === 'huggingface' ? 'purple.500' : borderColor}
                      borderRadius="md"
                      bg={settings.embeddingProvider === 'huggingface' ? 'purple.50' : 'transparent'}
                    >
                      <Radio value="huggingface" size="lg">
                        <VStack align="start" spacing={1} ml={2}>
                          <HStack>
                            <Text fontWeight="medium">HuggingFace API</Text>
                            <Badge colorScheme="green">무료 30K/월</Badge>
                            <Badge colorScheme="purple">고품질</Badge>
                          </HStack>
                          <Text fontSize="sm" color="gray.600">
                            • 월 30,000회 무료
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            • 빠른 응답 속도
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            • 384차원 임베딩 (향상된 정확도)
                          </Text>
                        </VStack>
                      </Radio>
                    </Box>
                  </Stack>
                </RadioGroup>
              </FormControl>

              {(settings.embeddingProvider === 'huggingface' || showApiKeyInput) && (
                <FormControl>
                  <FormLabel>
                    HuggingFace API Key
                    <Text fontSize="sm" color="gray.500" fontWeight="normal">
                      huggingface.co에서 무료로 발급받으세요
                    </Text>
                  </FormLabel>
                  <Input 
                    type="password"
                    placeholder={settings.huggingfaceApiKey ? '••••••••' : 'hf_...'}
                    value={settings.huggingfaceApiKey === '••••••••' ? '' : (settings.huggingfaceApiKey || '')}
                    onChange={(e) => setSettings({
                      ...settings,
                      huggingfaceApiKey: e.target.value
                    })}
                  />
                </FormControl>
              )}

              {settings.embeddingProvider === 'huggingface' && settings.embeddingUsageCount !== undefined && (
                <Stat 
                  p={4} 
                  border="1px" 
                  borderColor={borderColor} 
                  borderRadius="md"
                  bg={useColorModeValue('gray.50', 'gray.700')}
                >
                  <StatLabel>HuggingFace 사용량 (이번 달)</StatLabel>
                  <StatNumber>
                    {settings.embeddingUsageCount.toLocaleString()} / 30,000
                  </StatNumber>
                  <StatHelpText>
                    남은 무료 할당량: {(30000 - settings.embeddingUsageCount).toLocaleString()}회
                    {settings.embeddingUsageResetDate && (
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        다음 리셋: {new Date(settings.embeddingUsageResetDate).toLocaleDateString('ko-KR')}
                      </Text>
                    )}
                  </StatHelpText>
                </Stat>
              )}
              
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  임베딩 제공자를 변경하면 즉시 적용됩니다. HuggingFace 실패 시 자동으로 Xenova로 전환됩니다.
                </Text>
              </Alert>

              <FormControl>
                <FormLabel>
                  신뢰도 임계값
                  <Text fontSize="sm" color="gray.500" fontWeight="normal">
                    AI가 답변할 때 필요한 최소 신뢰도 (0.0 ~ 1.0)
                  </Text>
                </FormLabel>
                <Input 
                  type="number" 
                  value={settings.confidenceThreshold}
                  onChange={(e) => setSettings({
                    ...settings,
                    confidenceThreshold: parseFloat(e.target.value)
                  })}
                  min="0" 
                  max="1" 
                  step="0.1" 
                />
              </FormControl>

              <FormControl>
                <FormLabel>
                  최대 검색 결과 수
                  <Text fontSize="sm" color="gray.500" fontWeight="normal">
                    ChromaDB에서 가져올 문서 개수 (1 ~ 20)
                  </Text>
                </FormLabel>
                <Input 
                  type="number" 
                  value={settings.maxSearchResults}
                  onChange={(e) => setSettings({
                    ...settings,
                    maxSearchResults: parseInt(e.target.value)
                  })}
                  min="1" 
                  max="20" 
                />
              </FormControl>

              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <FormLabel mb="0">자동 답변 개선</FormLabel>
                  <Text fontSize="sm" color="gray.500">
                    AI가 검색 결과를 자연스럽게 개선하여 답변
                  </Text>
                </Box>
                <Switch 
                  isChecked={settings.autoImprovement}
                  onChange={(e) => setSettings({
                    ...settings,
                    autoImprovement: e.target.checked
                  })}
                  size="lg"
                />
              </FormControl>

            {settings.updatedAt && (
              <Text fontSize="xs" color="gray.500" textAlign="right">
                마지막 업데이트: {new Date(settings.updatedAt).toLocaleString('ko-KR')}
              </Text>
            )}
          </VStack>
            </AccordionPanel>
          </AccordionItem>
        )}

        {/* 액션 버튼 */}
        {(canConfigureAI || canConfigureEmbedding) && (
          <HStack spacing={4} px={4} py={4}>
            <Button 
              colorScheme="bexco" 
              onClick={handleSaveSettings}
              isLoading={isSaving}
              loadingText="저장 중..."
              isDisabled={isLoading}
              size="lg"
            >
              설정 저장
            </Button>
            <Button variant="outline" colorScheme="gray" onClick={loadSettings} isDisabled={isLoading}>
              새로고침
            </Button>
          </HStack>
        )}

        {/* 시스템 설정 (동의어, 패턴) - admin만 가능 */}
        {canEditSystemConfig && (
          <AccordionItem border="1px" borderColor={borderColor} borderRadius="md" mb={4} bg={cardBg}>
            <h2>
              <AccordionButton _expanded={{ bg: useColorModeValue('gray.50', 'gray.700') }}>
                <Box flex="1" textAlign="left">
                  <Heading size="md">⚙️ 시스템 설정</Heading>
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
            <VStack spacing={6} align="stretch">
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  동의어, 시설 패턴, 요금, 쿼리 필터를 관리합니다. 저장하면 서버 재시작 없이 즉시 적용됩니다.
                </Text>
              </Alert>

              <Tabs
                index={systemConfigTab}
                onChange={(index) => setSystemConfigTab(index)}
                colorScheme="blue"
                variant="enclosed"
              >
                <TabList>
                  {Object.keys(CONFIG_KEYS).map((key) => (
                    <Tab key={key}>
                      {CONFIG_KEYS[key as keyof typeof CONFIG_KEYS].title}
                    </Tab>
                  ))}
                </TabList>

                <TabPanels>
                  {Object.keys(CONFIG_KEYS).map((key) => (
                    <TabPanel key={key}>
                      <VStack spacing={4} align="stretch">
                        <Alert status="info" borderRadius="md" size="sm">
                          <AlertIcon />
                          <Box>
                            <Text fontWeight="bold" fontSize="sm">
                              {CONFIG_KEYS[key as keyof typeof CONFIG_KEYS].title}
                            </Text>
                            <Text fontSize="xs">
                              {CONFIG_KEYS[key as keyof typeof CONFIG_KEYS].description}
                            </Text>
                            {systemConfigMeta[key]?.updated_at && (
                              <Text fontSize="xs" color="gray.600" mt={1}>
                                마지막 수정: {new Date(systemConfigMeta[key].updated_at).toLocaleString('ko-KR')}
                                {systemConfigMeta[key].updated_by && ` (by ${systemConfigMeta[key].updated_by})`}
                              </Text>
                            )}
                          </Box>
                        </Alert>

                        {systemConfigLoading[key] ? (
                          <Box textAlign="center" py={10}>
                            <Spinner size="xl" />
                            <Text mt={4}>설정 불러오는 중...</Text>
                          </Box>
                        ) : (
                          <>
                            <Box>
                              <HStack justifyContent="space-between" mb={2}>
                                <Text fontWeight="bold" fontSize="sm">{key}.json</Text>
                                <HStack>
                                  <Button
                                    size="sm"
                                    onClick={() => fetchSystemConfig(key)}
                                    isLoading={systemConfigLoading[key]}
                                  >
                                    🔄 새로고침
                                  </Button>
                                  <Button
                                    size="sm"
                                    colorScheme="green"
                                    onClick={() => saveSystemConfig(key)}
                                    isLoading={systemConfigSaving[key]}
                                    isDisabled={!isSystemConfigJsonValid(key)}
                                  >
                                    💾 저장
                                  </Button>
                                </HStack>
                              </HStack>

                              <Textarea
                                value={systemConfigs[key]}
                                onChange={(e) => setSystemConfigs(prev => ({
                                  ...prev,
                                  [key]: e.target.value
                                }))}
                                fontFamily="monospace"
                                fontSize="sm"
                                minHeight="300px"
                                placeholder="설정을 불러오는 중..."
                                bg="gray.50"
                                borderColor={isSystemConfigJsonValid(key) ? 'gray.200' : 'red.500'}
                              />
                              {!isSystemConfigJsonValid(key) && systemConfigs[key] && (
                                <Text color="red.500" fontSize="sm" mt={1}>
                                  ⚠️ JSON 형식이 올바르지 않습니다.
                                </Text>
                              )}
                            </Box>

                            <Box p={3} bg="blue.50" borderRadius="md">
                              <Text fontWeight="bold" fontSize="sm" mb={1}>💡 사용처</Text>
                              <Text fontSize="xs">
                                {key === 'synonyms' && '• bexcoWorkflow.js - 키워드 확장 검색 (동의어 그룹)'}
                                {key === 'attribute_patterns' && '• conversationAgent.js - 속성 질문 감지 (운영시간, 요금 등)'}
                                {key === 'facility_file_patterns' && '• bexcoWorkflow.js + conversationAgent.js - 시설 키워드 감지 및 파일 패턴 매핑'}
                                {key === 'utilities' && '• conversationAgent.js - 부대시설 요금 안내 (주차료, 전기료 등)'}
                              </Text>
                            </Box>
                          </>
                        )}
                      </VStack>
                    </TabPanel>
                  ))}
                </TabPanels>
              </Tabs>
            </VStack>
            </AccordionPanel>
          </AccordionItem>
        )}

      </Accordion>
    </VStack>
  )
}

export default SettingsPage






